import React from "react";
import { useSelector } from "react-redux";

import {
  Grid,
  Box,
  Typography,
  makeStyles,
  TableContainer,
  Table,
  TableBody,
  TableHead,
} from "@material-ui/core";

import { headerRows, bodyRows } from "./sample";
import { COLORS } from "../../../../theme/colors";
import { ActionButtonsGroup } from "../../../../components/buttonsGroup/ActionButtonsGroup";
import { StyledTableRow, StyledTableCell } from "../../../../components/base/Table";
import { capitalize } from "../../../../services";

const useStyles = makeStyles((theme) => ({
  title: {
    margin: `${theme.spacing(3.5)}px 0 ${theme.spacing(2.5)}px`,
  },
  tableContainer: {
    borderRadius: 9,
    border: `1px solid ${COLORS.darks[0][10]}`,
    boxShadow: "none",
  },
  caption: {
    fontWeight: 700,
  },
  cell: {
    "&:not(:last-child)": {
      borderRight: `1px solid ${COLORS.darks[0][10]}`,
    },
  },
  row: {
    backgroundColor: COLORS.white,
    "&:last-child": {
      backgroundColor: "#A2CA8F",
    },
    "&:nth-last-child(3)": {
      backgroundColor: COLORS.darks[0][10],
    },
  },
  lastChildRow: {
    "& p": {
      fontWeight: 700,
    },
  },
  assumptions: {
    borderRight: `1px solid ${COLORS.darks[0][50]}`,
    borderLeft: `1px solid ${COLORS.darks[0][50]}`,
  },
  footer: {
    marginTop: theme.spacing(5.75),
    marginBottom: theme.spacing(1.75),
  },
  groupTitle: {
    "&.child-rows": {
      fontWeight: 300,
      textTransform: "uppercase",
    },
    "&:not(.child-rows)": {
      fontWeight: 700,
    },
    "&.transparent": {
      color: "transparent",
    },
  },
  purpleBackground: {
    backgroundColor: "#FCF2FA",
  },
}));

export const CashFlowForecastTab = () => {
  const classes = useStyles();
  const { user } = useSelector((state) => state.auth);
  const currencySymbol = user.currency.symbol;

  return (
    <Box>
      <Grid container alignItems="center" justifyContent="space-between" className={classes.title}>
        <Typography variant="body1" style={{ fontWeight: 700 }}>
          CASH FLOW FORECAST
        </Typography>
        <ActionButtonsGroup hideIntegrateBtn />
      </Grid>
      <TableContainer className={classes.tableContainer}>
        <Table>
          <TableHead>
            <StyledTableRow>
              <StyledTableCell></StyledTableCell>
              {headerRows.map((row, i) => (
                <React.Fragment key={i}>
                  <StyledTableCell>
                    <Typography variant="subtitle2" className={classes.caption}>
                      {row}
                    </Typography>
                  </StyledTableCell>
                </React.Fragment>
              ))}
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {bodyRows.map((row, i) => (
              <React.Fragment key={i}>
                <StyledTableRow className={classes.row}>
                  <StyledTableCell colspan={!row.assumptions ? headerRows.length + 1 : 0}>
                    <Typography
                      variant="body1"
                      className={`
                      ${classes.groupTitle}
                      ${row.childRows ? "child-rows" : ""}
                      ${row.title === "empty" ? "transparent" : ""}
                      `}
                    >
                      {row.title}
                    </Typography>
                  </StyledTableCell>
                  {row.assumptions ? (
                    <React.Fragment>
                      <StyledTableCell className={classes.assumptions}>
                        <Typography variant="body1">{row.assumptions}</Typography>
                      </StyledTableCell>
                      <StyledTableCell className={classes.cell}>
                        <Typography variant="body1"></Typography>
                      </StyledTableCell>
                      <StyledTableCell className={classes.cell}>
                        <Typography variant="body1"></Typography>
                      </StyledTableCell>
                      <StyledTableCell className={classes.cell}>
                        <Typography variant="body1"></Typography>
                      </StyledTableCell>
                      <StyledTableCell className={classes.cell}>
                        <Typography variant="body1"></Typography>
                      </StyledTableCell>
                      <StyledTableCell className={classes.cell}>
                        <Typography variant="body1"></Typography>
                      </StyledTableCell>
                      <StyledTableCell className={classes.cell}>
                        <Typography variant="body1"></Typography>
                      </StyledTableCell>
                      <StyledTableCell className={classes.cell}>
                        <Typography variant="body1"></Typography>
                      </StyledTableCell>
                      <StyledTableCell className={classes.cell}>
                        <Typography variant="body1"></Typography>
                      </StyledTableCell>
                      <StyledTableCell className={classes.cell}>
                        <Typography variant="body1"></Typography>
                      </StyledTableCell>
                    </React.Fragment>
                  ) : null}
                </StyledTableRow>
                {row.childRows
                  ? row.childRows.map((childRow, j) => (
                      <StyledTableRow
                        key={j}
                        className={`
                        ${classes.row}
                        ${row.childRows.length - 1 === j ? classes.lastChildRow : ""}
                        ${
                          [bodyRows.length - 4, bodyRows.length - 5].includes(i)
                            ? classes.purpleBackground
                            : ""
                        }
                      `}
                      >
                        <StyledTableCell>
                          <Typography variant="body1">{childRow.childTitle}</Typography>
                        </StyledTableCell>
                        <StyledTableCell className={classes.assumptions}>
                          <Typography variant="body1">
                            {childRow.assumptions === `XXX ${currencySymbol}/Sqft`
                              ? `XXX ${currencySymbol}/${capitalize(user?.unit_system)}`
                              : childRow.assumptions}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell className={classes.cell}>
                          <Typography variant="body1"></Typography>
                        </StyledTableCell>
                        <StyledTableCell className={classes.cell}>
                          <Typography variant="body1"></Typography>
                        </StyledTableCell>
                        <StyledTableCell className={classes.cell}>
                          <Typography variant="body1"></Typography>
                        </StyledTableCell>
                        <StyledTableCell className={classes.cell}>
                          <Typography variant="body1"></Typography>
                        </StyledTableCell>
                        <StyledTableCell className={classes.cell}>
                          <Typography variant="body1"></Typography>
                        </StyledTableCell>
                        <StyledTableCell className={classes.cell}>
                          <Typography variant="body1"></Typography>
                        </StyledTableCell>
                        <StyledTableCell className={classes.cell}>
                          <Typography variant="body1"></Typography>
                        </StyledTableCell>
                        <StyledTableCell className={classes.cell}>
                          <Typography variant="body1"></Typography>
                        </StyledTableCell>
                        <StyledTableCell className={classes.cell}>
                          <Typography variant="body1"></Typography>
                        </StyledTableCell>
                      </StyledTableRow>
                    ))
                  : null}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Grid container alignItems="right" className={classes.footer}>
        <ActionButtonsGroup hideIntegrateBtn />
      </Grid>
    </Box>
  );
};
